<template>
  <div class="heading">
    <h3>{{ pageTitle }}</h3>
    <!-- Update (admins and editors; Seán, 2026-10-31): a quiet refresh mark on the heading's
         baseline, no box; it spins while the sheet is fetched, the words beside it saying so -->
    <template v-if="isSignedIn && isStaff">
      <button
        type="button"
        class="refresh"
        :class="{isUpdating}"
        title="Fetch the latest data from Google Sheets"
        aria-label="Update from Google Sheets"
        :disabled="isUpdating"
        @click="updateSheet"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M20 12a8 8 0 1 1-2.3-5.7" />
          <path d="M20 4v5h-5" />
        </svg>
      </button>
      <!-- the film's bird (Seán, 2026-11-05): on (brand green), its card in /Videos and the
           program's ▶ go to the film; off (faint), both to the preview (useFilmListings.js) -->
      <button
        v-for="film in yearFilms"
        :key="film.key"
        type="button"
        class="refresh bird"
        :class="{isOn: getIsListed(film.key)}"
        :title="getBirdTitle(film)"
        :aria-label="`List ${film.title}`"
        :aria-pressed="getIsListed(film.key)"
        :disabled="isSavingFilm"
        @click="toggleFilm(film)"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M2 12c2.5-4 6-4.5 10 1.5 4-6 7.5-5.5 10-1.5" />
        </svg>
      </button>
      <span v-if="note" class="note" :class="note.kind" role="status">{{ note.text }}</span>
    </template>
  </div>
  <ProgramTable :show-day-tags="true" />
</template>

<script setup>
import history from "@/assets/history.json";
import {computed, ref} from "vue";
import {useRoute} from "vue-router";
import ProgramTable from "@/components/Program/Table.vue";
import useRoles from "@/composables/useRoles";
import useUserSafe from "@/composables/useUserSafe";
import {authFetch, readJson} from "@/composables/useAuthFetch";
import {setProgramSheet} from "@/composables/useProgramSheet";
import useFilmListings from "@/composables/useFilmListings";
import films from "@/assets/videos/films.json";
const UPDATED_SHOWN = 3000; // ms "Updated" stays
const route = useRoute();
const {isSignedIn} = useUserSafe();
const {isStaff} = useRoles();
const isUpdating = ref(false);
const note = ref(null);
let clearNoteTimer = null;
const {getIsListed, setIsListed} = useFilmListings();
const isSavingFilm = ref(false);
// this year's films with a program row (assets/videos/films.json), e.g. 2026's The Birds
const yearFilms = computed(() => Object.values(films)
  .filter((film) => film.program?.from.startsWith(String(route.params.year)))
  .map((film) => ({key: film.key, title: film.name.split(": ").at(-1)})));
const pageTitle = computed(() => {
  let pageTitle = "Program";
  if (history.programFinalized) return pageTitle;
  return `Preliminary ${pageTitle}`;
});

// the answer's note: "Updated", or "Already up to date" when the sheet is as served (Seán,
// 2026-11-01), goes by itself; not committed (orange: look here) and a failure (red) stay until
// the next press
function showResult(result) {
  if (result.committed === false) {
    note.value = {kind: "notCommitted", text: `Updated; not committed: ${result.error || "unknown"}`};
    return;
  }
  const text = result.changed === false ? "Already up to date" : "Updated";
  note.value = {kind: "updated", text};
  clearNoteTimer = setTimeout(() => (note.value = null), UPDATED_SHOWN);
}

const BIRD_ON = "on. Shown in Videos; the program's ▶ opens the film. Click to turn off.";
const BIRD_OFF = "off. In Videos and the program, its ▶ opens the preview. Click to turn on.";
const getBirdTitle = ({key, title}) => `${title}: ${getIsListed(key) ? BIRD_ON : BIRD_OFF}`;

async function toggleFilm({key}) {
  isSavingFilm.value = true;
  await setIsListed(key, !getIsListed(key));
  isSavingFilm.value = false;
}

async function updateSheet() {
  const year = route.params.year;
  clearTimeout(clearNoteTimer);
  isUpdating.value = true;
  note.value = {kind: "fetching", text: "Fetching Google Sheets…"};
  const response = await authFetch(`/api/v1/admin/program/${year}/update`, {method: "POST"});
  const result = await readJson(response);
  isUpdating.value = false;
  if (!response.ok) {
    const message = result.statusMessage || result.message || response.statusText;
    note.value = {kind: "failed", text: `Couldn't update: ${message || "unknown error"}`};
    return;
  }
  setProgramSheet(year, result.data);
  showResult(result);
}
</script>

<style scoped>
/* the heading and, for staff, the refresh mark and its words on its baseline */
.heading {
  float: left;
  display: flex;
  align-items: baseline;
  gap: 0.5em;
}
h3 {
  display: inline;
  margin: 0;
}
/* the mark: an aside (DESIGN.md § 2), not a button's look; the arrow's foot on the baseline */
.refresh {
  padding: 0;
  margin: 0;
  background: none;
  border: none;
  box-shadow: none;
  border-radius: 0;
  line-height: 0;
  font-size: 1rem;
  color: #8a8a8a;
  cursor: pointer;
}
.refresh svg {
  width: 1.05em;
  height: 1.05em;
  vertical-align: -0.1em;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.4;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.refresh:hover:not(:disabled) {
  filter: brightness(1.3); /* a hover in the aside's own colour, not the brand's (§ 15) */
}
.refresh:focus-visible {
  outline: 2px solid var(--brand-color);
  outline-offset: 3px;
}
.refresh:disabled {
  cursor: default;
}
.refresh.isUpdating svg {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .refresh.isUpdating svg {
    animation: none;
    opacity: 0.5;
  }
}
/* the bird: faint when off, the brand's green when on */
.bird {
  opacity: 0.45;
}
.bird svg {
  width: 1.3em;
  height: 1.3em;
}
.bird.isOn {
  opacity: 1;
  color: var(--brand-color);
}
.note {
  font-size: 0.85rem;
  color: #8a8a8a;
}
.notCommitted {
  color: var(--orange-on-light);
}
.failed {
  color: #c62828;
}
.darkMode .notCommitted {
  color: var(--orange-on-dark);
}
.darkMode .failed {
  color: #ff6b6b;
}
/* the time zone menu (Program/TimeZone.vue, floated right) on the heading's line, its text on the
   heading's baseline (Seán, 2026-10-29), as Key Dates': moved, not spaced */
.heading ~ :deep(#timezone) {
  margin-top: 0;
  position: relative;
  top: 8px;
}
/* on a phone the heading follows the menu's margin down */
.narrow .heading ~ :deep(#timezone) {
  top: 14px;
}
</style>
